import { paymentStatementApi } from './base';
import { getRefreshToken } from '../utils/session';

export interface LoginRequest {
	email: string;
	password: string;
}

export interface RegisterRequest {
	email: string;
	password: string;
	name: string;
	enterprise_id?: string;
	invited_email?: string;
}

export interface UserData {
	id: string;
	email: string;
	name: string;
	enterprise_id: string;
	timezone: string;
	roles: string[];
	permissions: string[];
}

export interface AuthResponse {
	success: boolean;
	message: string;
	token: string;
	refresh_token?: string;
	user: UserData;
	expires_at: string;
}

export interface TokenValidationResponse {
	success: boolean;
	message: string;
	user_id: string;
	email: string;
	name: string;
	enterprise_id: string;
	timezone: string;
	roles: string[];
	permissions: string[];
	tenant_id?: string;
	validated_at: string;
}

export interface UserInfoResponse {
	success: boolean;
	message: string;
	user_id: string;
	email: string;
	name: string;
	enterprise_id: string;
	tenant_id?: string;
	timezone: string;
	retrieved_at: string;
}

export interface AuthStatusResponse {
	success: boolean;
	message: string;
	authenticated: boolean;
	user_id: string | null;
	token_type: string;
	timestamp: string;
}

export interface ForgotPasswordRequest {
	email: string;
}

export interface ResetPasswordRequest {
	token: string;
	new_password: string;
}

export interface ResetPasswordLookupResponse {
	success: boolean;
	email?: string;
	user_id?: string;
	message?: string;
}

export interface GoogleLoginResponse {
	success: boolean;
	auth_url: string;
	state: string;
}

export interface GoogleBounceResponse {
	success: boolean;
	return_origin: string | null;
}

export interface GoogleCallbackRequest {
	code: string;
	state?: string; // OAuth state parameter (contains tenant info for localhost subdomain support)
}

export interface GoogleLoginResponse {
	success: boolean;
	auth_url: string;
	state: string;
}

export const authApi = paymentStatementApi.injectEndpoints({
	endpoints: (builder) => ({
		login: builder.mutation<AuthResponse, LoginRequest>({
			query: (body) => ({
				url: '/auth/login',
				method: 'POST',
				body,
			}),
		}),

		register: builder.mutation<
			AuthResponse | { success: boolean; message: string },
			RegisterRequest
		>({
			query: (body) => ({
				url: '/auth/register',
				method: 'POST',
				body,
			}),
		}),

		validateToken: builder.query<TokenValidationResponse, void>({
			query: () => ({
				url: '/auth/validate',
				method: 'GET',
			}),
		}),

		getUserInfo: builder.query<UserInfoResponse, void>({
			query: () => ({
				url: '/auth/user',
				method: 'GET',
			}),
			providesTags: ['UserInfo'],
		}),

		getAuthStatus: builder.query<AuthStatusResponse, void>({
			query: () => ({
				url: '/auth/status',
				method: 'GET',
			}),
		}),

		logout: builder.mutation<{ success: boolean; message: string }, void>({
			query: () => ({
				url: '/auth/logout',
				method: 'POST',
				body: { refresh_token: getRefreshToken() },
			}),
			invalidatesTags: ['UserInfo'],
		}),

		verifyEmail: builder.mutation<
			AuthResponse | { success: boolean; message: string },
			string
		>({
			query: (token) => ({
				url: `/auth/verify-email?token=${token}`,
				method: 'GET',
			}),
		}),

		forgotPassword: builder.mutation<
			{ success: boolean; message: string },
			ForgotPasswordRequest
		>({
			query: (body) => ({
				url: '/auth/forgot-password',
				method: 'POST',
				body,
			}),
		}),

		resetPassword: builder.mutation<AuthResponse, ResetPasswordRequest>({
			query: (body) => ({
				url: '/auth/reset-password',
				method: 'POST',
				body,
			}),
		}),

		lookupResetToken: builder.query<ResetPasswordLookupResponse, string>({
			query: (token) => ({
				url: `/auth/reset-password/lookup?token=${token}`,
				method: 'GET',
			}),
		}),

		googleLogin: builder.mutation<GoogleLoginResponse, void>({
			query: () => ({
				url: '/auth/google/login',
				method: 'GET',
			}),
		}),

		googleBounce: builder.mutation<GoogleBounceResponse, string>({
			query: (state) => ({
				url: '/auth/google/bounce',
				method: 'GET',
				params: { state },
			}),
		}),

		googleCallback: builder.mutation<AuthResponse, GoogleCallbackRequest>({
			query: (body) => ({
				url: '/auth/google/callback',
				method: 'POST',
				body,
			}),
		}),
	}),
});

export const {
	useLoginMutation,
	useRegisterMutation,
	useValidateTokenQuery,
	useGetUserInfoQuery,
	useGetAuthStatusQuery,
	useLogoutMutation,
	useVerifyEmailMutation,
	useForgotPasswordMutation,
	useResetPasswordMutation,
	useLookupResetTokenQuery,
	useGoogleLoginMutation,
	useGoogleBounceMutation,
	useGoogleCallbackMutation,
} = authApi;
